import LiveStatusBadge from "../../../Component/UI/LiveStatusBadge";
import styled from 'styled-components';

type EndedLiveProps = {
  title?: string;
  storeName?: string;
  onBrowse: () => void;
};

export default function EndedLive({ title, storeName, onBrowse }: EndedLiveProps) {
  return (
    <Page>
      <Header>
        <span>라이브</span>
        <CloseButton type="button" onClick={onBrowse} aria-label="라이브 목록으로">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </CloseButton>
      </Header>

      <Content aria-labelledby="ended-live-heading">
        <EndIllustration viewBox="0 0 120 108" fill="none" aria-hidden="true">
          <rect x="8" y="10" width="104" height="76" rx="20" fill="var(--user-accent-soft)" stroke="var(--user-accent)" />
          <path d="M8 32h104" stroke="var(--user-accent)" />
          <circle cx="23" cy="21" r="2" fill="var(--user-accent)" />
          <circle cx="31" cy="21" r="2" fill="#8b73b3" />
          <circle cx="39" cy="21" r="2" fill="#8b73b3" />
          <path d="M60 69 46 56a8 8 0 0 1 14-8 8 8 0 0 1 14 8L60 69Z" fill="var(--user-accent)" />
          <path d="M48 86v12m24-12v12M40 98h40" stroke="#8b73b3" strokeWidth="2" strokeLinecap="round" />
        </EndIllustration>
        <Status status="종료" />
        <Heading id="ended-live-heading">라이브가 종료되었어요</Heading>
        <Description>함께해 주셔서 감사합니다.<br />다음 라이브에서 또 만나요!</Description>

        {title && (
          <Broadcast>
            {storeName && <StoreName>{storeName}</StoreName>}
            <BroadcastTitle>{title}</BroadcastTitle>
          </Broadcast>
        )}

        <BrowseButton type="button" onClick={onBrowse}>
          다른 라이브 보기
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M5 12h14m-5-5 5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </BrowseButton>
      </Content>
    </Page>
  );
}

const Page = styled.main`
  --ended-background: var(--user-surface);
  --ended-foreground: var(--user-text);
  --ended-muted: var(--user-text-muted);
  --ended-border: var(--user-border);
  --ended-accent: var(--user-button);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: max(16px, env(safe-area-inset-top)) 24px max(24px, env(safe-area-inset-bottom));
  background: var(--ended-background);
  color: var(--ended-foreground);
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
  background: var(--ended-background);
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 44px;
  font-family: var(--font-Pretendard-SemiBold);
  font-size: 16px;
`;

const CloseButton = styled.button`
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  margin-right: -10px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--ended-muted);
  cursor: pointer;
  &:hover { background: var(--ended-border); color: var(--ended-foreground); }
  &:focus-visible { outline: 2px solid var(--user-accent); outline-offset: 3px; }
`;

const Content = styled.section`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 352px;
  margin: 0 auto;
  padding: 48px 0 80px;
  text-align: center;
`;

const EndIllustration = styled.svg`
  flex-shrink: 0;
  width: 120px;
  height: 108px;
  margin-bottom: 28px;
`;

const Status = styled(LiveStatusBadge)`
  margin-bottom: 12px;
`;

const Heading = styled.h1`
  margin: 0;
  font-family: var(--font-Pretendard-Bold);
  font-size: clamp(23px, 6vw, 28px);
  font-weight: normal;
  line-height: 1.4;
  letter-spacing: -0.8px;
  word-break: keep-all;
`;

const Description = styled.p`
  margin: 16px 0 0;
  color: var(--ended-muted);
  font-size: 16px;
  line-height: 1.7;
  word-break: keep-all;
`;

const Broadcast = styled.div`
  box-sizing: border-box;
  width: 100%;
  margin-top: 32px;
  padding: 18px 12px;
  border-top: 1px solid var(--ended-border);
  border-bottom: 1px solid var(--ended-border);
  overflow-wrap: anywhere;
`;

const StoreName = styled.p`
  margin: 0 0 6px;
  color: var(--ended-muted);
  font-size: 13px;
  line-height: 1.5;
`;

const BroadcastTitle = styled.p`
  margin: 0;
  font-family: var(--font-Pretendard-Medium);
  font-size: 14px;
  line-height: 1.6;
`;

const BrowseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-height: 54px;
  margin-top: 32px;
  padding: 14px 20px;
  border: 0;
  border-radius: 14px;
  background: var(--ended-accent);
  color: #fff;
  font-family: var(--font-Pretendard-SemiBold);
  font-size: 16px;
  line-height: 1.5;
  cursor: pointer;
  &:hover { background: #6842df; }
  &:active { background: #5831ca; }
  &:focus-visible { outline: 2px solid #d4c7ff; outline-offset: 4px; }
`;
